import { useEffect } from 'react';

import { ERROR_DEFAULT } from 'app/shared/consts/error-codes';
import { useErrors } from 'app/shared/contexts/errors';
import { useOpenParams } from 'app/shared/hooks/use-open-params/use-open-params.hook';

export const OPEN_APP_PARAMS_GUARD_KEY = 'open-app-params-guard';

/** Query-side fields the host iframe MUST pass for the app to be useful. */
const REQUIRED_QUERY_PARAMS = ['authToken', 'apiUrl', 'userBuildingUuid'] as const;

/**
 * Asserts that the iframe was opened with the required query params. If any
 * are missing, raises a terminal error on the `genericErrors` channel so
 * `<ErrorNavigator>` routes the user to `/error`. Without these the
 * foundational queries can't fire (their `enabled` flags depend on
 * `apiUrl` / `userBuildingUuid`), so the app would otherwise drop into a
 * silent broken state.
 *
 * Renders nothing. Mount once inside `AppContentWrapper` — runs on mount,
 * re-checks when the params change, and clears the error if the params
 * later populate (e.g. localStorage hydrated on a slow first render).
 */
export const OpenAppParamsGuard = (): null => {
  const params = useOpenParams();
  const { addGenericError, hideGenericErrors } = useErrors();

  useEffect(() => {
    const missing = REQUIRED_QUERY_PARAMS.filter((key) => !params[key]);
    if (missing.length > 0) {
      addGenericError(OPEN_APP_PARAMS_GUARD_KEY, {
        error: new Error(`Missing required open-app params: ${missing.join(', ')}`),
        errorCode: ERROR_DEFAULT,
      });
    } else {
      hideGenericErrors(OPEN_APP_PARAMS_GUARD_KEY);
    }
  }, [params.authToken, params.apiUrl, params.userBuildingUuid, addGenericError, hideGenericErrors, params]);

  return null;
};
